import { attributes, AttributesMixin, Collection, define, Model } from "@type-r/models";
import { useIO, useModel } from "@type-r/react";
import { CollapsableSection, FormSelect, Loading, Option, Page, PageContainer, InlineTable, TableHeader, TableCell, asDate, div } from "lqx-react";
import * as React from "react";
import { restfulIO } from "api/endpoint";
import { TableRow } from "@material-ui/core";

@define
class PaymentStats extends Model {
    static endpoint = restfulIO('/api/v2/recs/payments/metrics');

    static attributes = {
        arHasConvId: 0,
        hasAutoMatches: 0,
        hasAutoRecs: 0,
        isIntraDay : 0,
        isPriorDay : 0,
        matchedWithRi : 0,
        matchedWithRa : 0,
        matchedWithHeuristic : 0,
        hasConvId: 0,
        hasResolvedInvoices: 0,
        time: Date,
        total: 0,

        ar : 0,
        hasResolvedRA : 0,
        hasRA : 0,
        hasInvoices : 0,
    }
}

interface PaymentStats extends AttributesMixin<typeof PaymentStats> {}

@define
class RemittanceAdviceStats extends Model {
    static endpoint = restfulIO('/api/v2/recs/remittance-advice/metrics');

    static attributes = {
        time: Date,
        total: 0,
        hasConvId: 0,
        hasResolvedInvoices: 0,
        hasAutoMatches: 0,
        hasInvoices : 0,
        hasPayments : 0,
        hasResolvedPayment : 0,
        hasAutoRecs: 0,
    }
}

interface RemittanceAdviceStats extends AttributesMixin<typeof RemittanceAdviceStats> {}

@define
class InvoicesStats extends Model {
    static endpoint = restfulIO('/api/v2/recs/invoice/metrics');

    static attributes = {
        time: Date,
        total: 0,
        hasConvId: 0,
        uniqueConversations: 575,
        withPayments: 0,
        withRas: 0,
        withResolvedPayment: 0,
        withResolvedRa: 0,
        matchedWithRi : 0,
        matchedWithRa : 0,
        matchedWithHeuristic : 0,        
        hasAutoMatches: 0,
        hasAutoRecs: 0
    }
}

interface InvoicesStats extends AttributesMixin<typeof InvoicesStats>{}

const MetricsState = attributes({
  payments : [ PaymentStats ],
  remittanceAdvices : [ RemittanceAdviceStats ],
  invoices : [ InvoicesStats ],
  interval : "day"
});

const paymentMetricsColumns = TileRows({
    'Date' : x => asDate( x.time ),
    'Total': Group( x => x.total, {
        'Ar' : Group( x => asShare( x.ar, x.total ), {
            'with Conv. id' : Group( x => asShare( x.arHasConvId, x.ar ), {
                'with invoices' : Group( x => asShare( x.hasInvoices, x.arHasConvId ), {
                    'with resolved RI' : x => asShare( x.hasResolvedInvoices, x.hasInvoices ),
                    'with RA' : Group( x => asShare( x.hasRA, x.hasInvoices ), {
                        'with resolved RA' : x => asShare( x.hasResolvedRA, x.hasRA )
                    }),
                    'auto-matched' : Group( x => asShare( x.hasAutoMatches, x.hasInvoices ), {
                        'with RI' : x => asShare( x.matchedWithRi, x.hasAutoMatches ),
                        'with RA' : x => asShare( x.matchedWithRa, x.hasAutoMatches ),
                        'with heuristic' : x => asShare( x.matchedWithHeuristic, x.hasAutoMatches ),
                        'auto-reconciled' : x => asShare( x.hasAutoRecs, x.hasAutoMatches )
                    }),
                })
            })
        }),
        'Non-Ar' : Group( x => asShare( x.total - x.ar, x.total ), {
            'with Conv. id' : x => asShare( x.hasConvId - x.arHasConvId, x.total - x.ar )
        })
    })
});

type Formatter = ( x : any ) => string;

function TileRows( items ){
    return Group( null, items );
}

function Group( value : Function, subitems : { [ name : string ] : any } ){
    let results : any = value ? [ value ] : [];
    
    for( let name in subitems ){
        let item = subitems[ name ];

        if( typeof item === 'function' ){
            results.push({ label : name, value : item, level: 0 });
        }
        else {
            results.push({ label : name, value : item[ 0 ], level : 0 });

            for( let i = 1; i < item.length; i++ ){
                item[ i ].level++;
                results.push(item[ i ]);
            }
        }
    }

    return results;
}

const raMetricsColumns = TileRows({
    'Date' : x => asDate( x.time ),
    'Total': Group( x => x.total, {
        'with Conv. id' : Group( x => asShare( x.hasConvId, x.total ), {
            'with invoices' : Group( x => asShare( x.hasInvoices, x.hasConvId ), {
                'with resolved invoices' : x => asShare( x.hasResolvedInvoices, x.hasInvoices )
            }),
            'with payments' : Group( x => asShare( x.hasPayments, x.hasConvId ), {
                'with resolved payment' : Group( x => asShare( x.hasResolvedPayment, x.hasPayments ), {
                    'auto-matched' : Group( x => asShare( x.hasAutoMatches, x.hasResolvedPayment ), {
                        'auto-reconciled' : x => asShare( x.hasAutoRecs, x.hasAutoMatches )
                    })
                })
            })
        })
    })
})

const invoiceMetricsColumns = TileRows({
    'Date' : x => asDate( x.time ),
    'Total': Group( x => x.total, {
        'with Conv. id' : Group( x => asShare( x.hasConvId, x.total ), {
            'with RA' : Group( x => asShare( x.withRas, x.hasConvId ), {
                'resolved in RA' : x => asShare( x.withResolvedRa, x.withRas ),
            }),

            'with Payments' : Group( x => asShare( x.withPayments, x.hasConvId ), {
                'resolved in RI' : x => asShare( x.withResolvedPayment, x.withPayments ),
                'auto-matched' : Group( x => asShare( x.hasAutoMatches, x.withPayments ), {
                    'with RI' : x => asShare( x.matchedWithRi, x.hasAutoMatches ),
                    'with RA' : x => asShare( x.matchedWithRa, x.hasAutoMatches ),
                    'with heuristic' : x => asShare( x.matchedWithHeuristic, x.hasAutoMatches ),
                    'auto-reconciled' : x => asShare( x.hasAutoRecs, x.hasAutoMatches )
                })
            }),

            'Unique conv.ids' : x => x.uniqueConversations
        })
    })
})

export const Metrics = () => {
  const state = useModel( MetricsState );

  const isReady = useIO( async () => {
    const options = { params : { type : state.interval }};
      await Promise.all([
        state.payments.fetch( options ),
        state.remittanceAdvices.fetch( options ),
        state.invoices.fetch( options )
      ])
  }, [ state.interval ]);

  return isReady ?
    <PageContainer>
      <Page>
        <FormSelect $value={ state.$.interval } label="Grouping interval">
            <Option value="day">Day</Option>
            <Option value="week">Week</Option>
            <Option value="month">Month</Option>
        </FormSelect>

        <CollapsableSection id="payments-metrics" title="payments">
            <Statistics columns={ paymentMetricsColumns } items={ state.payments } />
        </CollapsableSection>

        <CollapsableSection id="remittance-metrics" title="remittance advices">
            <Statistics columns={ raMetricsColumns } items={ state.remittanceAdvices } />
        </CollapsableSection>

        <CollapsableSection id="invoices-metrics" title="invoices">
            <Statistics columns={ invoiceMetricsColumns } items={ state.invoices } />
        </CollapsableSection>
      </Page>
    </PageContainer>
  :
    <Loading/>
}

function asShare( value, base ){
    return `${value} (${ value/base*100 | 0 }%)`
}

interface TableColumnSpec<T> {
    value : ( x : T ) => React.ReactNode,
    level? : number
}

function TableColumns<T>( spec : { [ label : string ] : TableColumnSpec<T> }) : ( TableColumnSpec<T> & { label : string } )[] {
    return Object.keys( spec ).map( label => ({ label, ...spec[ label ]}));
}


function Statistics<T extends Model>({ columns, items } : {
    items : Collection<T> | T[],
    columns : ( TableColumnSpec<T> & { label : string } )[]
}){

    return (
        <StatitsticsContainer>
            <StatisticsTile>
                { columns.map( ( col, i ) =>
                    <StatisticsValue key={ i } level={ col.level } >
                        { col.label }
                    </StatisticsValue>
                )}
            </StatisticsTile>

            {( items as any ).map( ( x, i ) =>
                <StatisticsTile key={ x.cid || x.id || i  }>
                    { columns.map( ( col, i  ) =>
                        <StatisticsValue key={ i }>
                            { col.value( x ) }
                        </StatisticsValue>
                    )}
                </StatisticsTile>
            )}
        </StatitsticsContainer>
    )
}

const StatitsticsContainer = div({
    display: 'flex',
    flexDirection: 'row',
    overflowX : 'scroll'
})

const StatisticsTile = div({
    display: 'flex',
    flexDirection: 'column',
    margin: 4
});

const StatisticsValue = div({
    fontSize: 12,
    whiteSpace: 'nowrap',
    marginLeft: ( props : any ) => props.level + 'em'
});


function InlineTableColumns<T extends Model>({ columns, items } : {
    items : Collection<T> | T[],
    columns : ( TableColumnSpec<T> & { label : string } )[]
}){
    return (
        <InlineTable
            head={
                <TableHeader>
                    { columns.map( ( col, i ) =>
                        <TableCell key={ i }>
                            { col.label }
                        </TableCell>
                    )}
                </TableHeader>
            }
        >
            { ( items as any ).map( ( x, i ) =>
                <TableRow key={ x.cid || x.id || i  }>
                    { columns.map( ( col, i  ) =>
                        <TableCell key={ i }>
                            { col.value( x ) }
                        </TableCell>
                    )}
                </TableRow>
            )}      
        </InlineTable>
    )
}